// GENERATED FILE — do not edit. Source of truth: contracts/. Regenerate: node scripts/sync-contracts.mjs

import { api } from '../apiClient';

import type { AxiosRequestConfig } from 'axios';

import { z } from 'zod';
// Runtime validation: every named type above has a matching `<Name>Schema` zod export — the
// exact schema the backend validates with (story 2-92), so forms can `.parse()`/`.safeParse()`
// against it instead of hand-rolling checks. `.refine()` cross-field rules are preserved
// verbatim; the refine() error MESSAGE is not — the server owns user-facing text.

export type ChangeRequestItem = {
  sectionId: string | null;
  fieldId: string | null;
  instanceId: string | null;
  message: string;
};
export const ChangeRequestItemSchema = z.object({
  sectionId: z.string().nullable().default(null),
  fieldId: z.string().nullable().default(null),
  instanceId: z.string().nullable().default(null),
  message: z.string().min(1).max(2000),
}).strict();

export type InspectionHistoryEntry = {
  at: string;
  by: string | null;
  from: "draft" | "scheduled" | "assigned" | "in_progress" | "submitted" | "in_review" | "approved" | "delivered" | "cancelled" | "completed";
  to: "draft" | "scheduled" | "assigned" | "in_progress" | "submitted" | "in_review" | "approved" | "delivered" | "cancelled" | "completed";
  note: string | null;
  reason: string | null;
  items: Array<ChangeRequestItem>;
};
export const InspectionHistoryEntrySchema = z.object({
  at: z.string(),
  by: z.string().nullable(),
  from: z.enum(["draft", "scheduled", "assigned", "in_progress", "submitted", "in_review", "approved", "delivered", "cancelled", "completed"] as const),
  to: z.enum(["draft", "scheduled", "assigned", "in_progress", "submitted", "in_review", "approved", "delivered", "cancelled", "completed"] as const),
  note: z.string().nullable(),
  reason: z.string().nullable(),
  items: z.array(ChangeRequestItemSchema),
}).strict();

export type InspectionAvailableAction = {
  action: "schedule" | "assign" | "start" | "submit" | "review" | "request_changes" | "approve" | "deliver" | "cancel";
  to: "draft" | "scheduled" | "assigned" | "in_progress" | "submitted" | "in_review" | "approved" | "delivered" | "cancelled" | "completed";
  allowed: boolean;
  reason: string | null;
  requirement: "scheduledFor" | "assignee" | "assigneeRole" | "note" | "reason" | null;
};
export const InspectionAvailableActionSchema = z.object({
  action: z.enum(["schedule", "assign", "start", "submit", "review", "request_changes", "approve", "deliver", "cancel"] as const),
  to: z.enum(["draft", "scheduled", "assigned", "in_progress", "submitted", "in_review", "approved", "delivered", "cancelled", "completed"] as const),
  allowed: z.boolean(),
  reason: z.string().nullable(),
  requirement: z.enum(["scheduledFor", "assignee", "assigneeRole", "note", "reason"] as const).nullable(),
}).strict();

export type InspectionDegradation = {
  code: "assignee_unavailable" | "type_inactive";
  message: string;
};
export const InspectionDegradationSchema = z.object({
  code: z.enum(["assignee_unavailable", "type_inactive"] as const),
  message: z.string(),
}).strict();

export type InspectionLifecycle = {
  id: string;
  status: "draft" | "scheduled" | "assigned" | "in_progress" | "submitted" | "in_review" | "approved" | "delivered" | "cancelled" | "completed";
  revision: number;
  attempt: number;
  assignedTo: string | null;
  assignedBy: string | null;
  teamId: string | null;
  scheduledFor: string | null;
  dueAt: string | null;
  startedAt: string | null;
  submittedAt: string | null;
  reviewStartedAt: string | null;
  approvedAt: string | null;
  approvedBy: string | null;
  deliveredAt: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
  cancelledBy: string | null;
  cancellationReason: string | null;
  readOnly: boolean;
  immutable: boolean;
  terminal: boolean;
  degradations: Array<InspectionDegradation>;
  actions: Array<InspectionAvailableAction>;
  history: Array<InspectionHistoryEntry>;
};
export const InspectionLifecycleSchema = z.object({
  id: z.string(),
  status: z.enum(["draft", "scheduled", "assigned", "in_progress", "submitted", "in_review", "approved", "delivered", "cancelled", "completed"] as const),
  revision: z.number().int(),
  attempt: z.number().int(),
  assignedTo: z.string().nullable(),
  assignedBy: z.string().nullable(),
  teamId: z.string().nullable(),
  scheduledFor: z.string().nullable(),
  dueAt: z.string().nullable(),
  startedAt: z.string().nullable(),
  submittedAt: z.string().nullable(),
  reviewStartedAt: z.string().nullable(),
  approvedAt: z.string().nullable(),
  approvedBy: z.string().nullable(),
  deliveredAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  cancelledAt: z.string().nullable(),
  cancelledBy: z.string().nullable(),
  cancellationReason: z.string().nullable(),
  readOnly: z.boolean(),
  immutable: z.boolean(),
  terminal: z.boolean(),
  degradations: z.array(InspectionDegradationSchema),
  actions: z.array(InspectionAvailableActionSchema),
  history: z.array(InspectionHistoryEntrySchema),
}).strict();

export type TransitionInspectionBody = {
  to: "draft" | "scheduled" | "assigned" | "in_progress" | "submitted" | "in_review" | "approved" | "delivered" | "cancelled" | "completed";
  action?: "schedule" | "assign" | "start" | "submit" | "review" | "request_changes" | "approve" | "deliver" | "cancel" | undefined;
  expectedRevision: number;
  note?: string | undefined;
  items?: Array<ChangeRequestItem> | undefined;
  reason?: string | undefined;
  assignedTo?: string | undefined;
  scheduledFor?: string | undefined;
  dueAt?: string | undefined;
  idempotencyKey?: string | undefined;
};
export const TransitionInspectionBodySchema = z.object({
  to: z.enum(["draft", "scheduled", "assigned", "in_progress", "submitted", "in_review", "approved", "delivered", "cancelled", "completed"] as const),
  action: z.enum(["schedule", "assign", "start", "submit", "review", "request_changes", "approve", "deliver", "cancel"] as const).optional(),
  expectedRevision: z.number().int().min(0),
  note: z.string().min(1).max(4000).optional(),
  items: z.array(ChangeRequestItemSchema).optional(),
  reason: z.string().min(1).max(4000).optional(),
  assignedTo: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId").optional(),
  scheduledFor: z.string().min(1).optional(),
  dueAt: z.string().min(1).optional(),
  idempotencyKey: z.string().min(8).max(128).optional(),
}).strict();

export type InspectionRevisionConflict = {
  reason: "revision_conflict";
  expectedRevision: number;
  currentRevision: number;
  current: InspectionLifecycle;
};
export const InspectionRevisionConflictSchema = z.object({
  reason: z.literal("revision_conflict"),
  expectedRevision: z.number().int(),
  currentRevision: z.number().int(),
  current: InspectionLifecycleSchema,
}).strict();

export type InspectionTransitionRefusal = {
  reason: "illegal_transition" | "transition_blocked";
  current: "draft" | "scheduled" | "assigned" | "in_progress" | "submitted" | "in_review" | "approved" | "delivered" | "cancelled" | "completed";
  from: "draft" | "scheduled" | "assigned" | "in_progress" | "submitted" | "in_review" | "approved" | "delivered" | "cancelled" | "completed";
  to: "draft" | "scheduled" | "assigned" | "in_progress" | "submitted" | "in_review" | "approved" | "delivered" | "cancelled" | "completed";
  action: "schedule" | "assign" | "start" | "submit" | "review" | "request_changes" | "approve" | "deliver" | "cancel";
  allowed: Array<"draft" | "scheduled" | "assigned" | "in_progress" | "submitted" | "in_review" | "approved" | "delivered" | "cancelled" | "completed">;
  allowedActions: Array<"schedule" | "assign" | "start" | "submit" | "review" | "request_changes" | "approve" | "deliver" | "cancel">;
  requirement: "scheduledFor" | "assignee" | "assigneeRole" | "note" | "reason" | null;
};
export const InspectionTransitionRefusalSchema = z.object({
  reason: z.enum(["illegal_transition", "transition_blocked"] as const),
  current: z.enum(["draft", "scheduled", "assigned", "in_progress", "submitted", "in_review", "approved", "delivered", "cancelled", "completed"] as const),
  from: z.enum(["draft", "scheduled", "assigned", "in_progress", "submitted", "in_review", "approved", "delivered", "cancelled", "completed"] as const),
  to: z.enum(["draft", "scheduled", "assigned", "in_progress", "submitted", "in_review", "approved", "delivered", "cancelled", "completed"] as const),
  action: z.enum(["schedule", "assign", "start", "submit", "review", "request_changes", "approve", "deliver", "cancel"] as const),
  allowed: z.array(z.enum(["draft", "scheduled", "assigned", "in_progress", "submitted", "in_review", "approved", "delivered", "cancelled", "completed"] as const)),
  allowedActions: z.array(z.enum(["schedule", "assign", "start", "submit", "review", "request_changes", "approve", "deliver", "cancel"] as const)),
  requirement: z.enum(["scheduledFor", "assignee", "assigneeRole", "note", "reason"] as const).nullable(),
}).strict();

export type SubmitIssue = {
  code: "missing_required" | "xor" | "union" | "allOf" | "dependsOn";
  message: string;
  sectionId: string | null;
  fieldId: string | null;
  instanceId: string | null;
};
export const SubmitIssueSchema = z.object({
  code: z.enum(["missing_required", "xor", "union", "allOf", "dependsOn"] as const),
  message: z.string(),
  sectionId: z.string().nullable(),
  fieldId: z.string().nullable(),
  instanceId: z.string().nullable(),
}).strict();

export type SubmitIncompleteRefusal = {
  reason: "submit_incomplete";
  issues: Array<SubmitIssue>;
};
export const SubmitIncompleteRefusalSchema = z.object({
  reason: z.literal("submit_incomplete"),
  issues: z.array(SubmitIssueSchema),
}).strict();

export type InspectionListPropertyRef = {
  _id: string;
  name: string | null;
};
export const InspectionListPropertyRefSchema = z.object({
  _id: z.string(),
  name: z.string().nullable(),
}).strict();

export type InspectionListItem = {
  _id: string;
  id: string;
  organizationId: string;
  status: "draft" | "scheduled" | "assigned" | "in_progress" | "submitted" | "in_review" | "approved" | "delivered" | "cancelled" | "completed";
  propertyId: InspectionListPropertyRef | null;
  roomsCount: number;
  aiSummary: string | null;
  scheduledFor: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string | null;
  updatedAt: string;
};
export const InspectionListItemSchema = z.object({
  _id: z.string(),
  id: z.string(),
  organizationId: z.string(),
  status: z.enum(["draft", "scheduled", "assigned", "in_progress", "submitted", "in_review", "approved", "delivered", "cancelled", "completed"] as const),
  propertyId: InspectionListPropertyRefSchema.nullable(),
  roomsCount: z.number().int(),
  aiSummary: z.string().nullable(),
  scheduledFor: z.string().nullable(),
  startedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  createdAt: z.string().nullable(),
  updatedAt: z.string(),
}).strict();

export type ListInspectionsQuery = {
  cursor?: string | undefined;
  limit?: number | undefined;
  sort?: string | undefined;
  status?: "draft" | "scheduled" | "assigned" | "in_progress" | "submitted" | "in_review" | "approved" | "delivered" | "cancelled" | "completed" | undefined;
  propertyId?: string | undefined;
};
export const ListInspectionsQuerySchema = z.object({
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  sort: z.string().min(1).optional(),
  status: z.enum(["draft", "scheduled", "assigned", "in_progress", "submitted", "in_review", "approved", "delivered", "cancelled", "completed"] as const).optional(),
  propertyId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId").optional(),
});

export type TemplateRef = {
  presetId: string;
  version?: number | undefined;
  schemaHash?: string | undefined;
};
export const TemplateRefSchema = z.object({
  presetId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
  version: z.number().int().min(1).optional(),
  schemaHash: z.string().min(1).max(128).optional(),
}).strict();

export type CreateInspectionFields = CreateInspectionBody;
export const CreateInspectionFieldsSchema = z.object({
  subjectId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId").optional(),
  propertyId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId").optional(),
  inspectionTypeId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId").optional(),
  templateRef: TemplateRefSchema.optional(),
  reportPresetId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId").optional(),
  rooms: z.array(z.object({
    name: z.string().min(1).max(200),
    displayOrder: z.number().int().min(0).optional(),
  }).strict()).optional(),
  scheduledFor: z.string().min(1).optional(),
  timezone: z.string().min(1).max(64).optional(),
  assignedTo: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId").optional(),
  dueAt: z.string().min(1).optional(),
  idempotencyKey: z.string().min(8).max(128).optional(),
}).strict();

export type CreateInspectionBody = {
  subjectId?: string | undefined;
  propertyId?: string | undefined;
  inspectionTypeId?: string | undefined;
  templateRef?: TemplateRef | undefined;
  reportPresetId?: string | undefined;
  rooms?: Array<{
  name: string;
  displayOrder?: number | undefined;
}> | undefined;
  scheduledFor?: string | undefined;
  timezone?: string | undefined;
  assignedTo?: string | undefined;
  dueAt?: string | undefined;
  idempotencyKey?: string | undefined;
};
export const CreateInspectionBodySchema = CreateInspectionFieldsSchema.refine((b) => Boolean(b.subjectId || b.propertyId));

export type InspectionTemplatePin = {
  templateId: string | null;
  version: number | null;
  schemaHash: string | null;
  name: string | null;
};
export const InspectionTemplatePinSchema = z.object({
  templateId: z.string().nullable(),
  version: z.number().int().nullable(),
  schemaHash: z.string().nullable(),
  name: z.string().nullable(),
}).strict();

export type CreatedInspection = {
  _id: string;
  id: string;
  organizationId: string;
  subjectId: string | null;
  propertyId: string | null;
  inspectionTypeId: string | null;
  status: "draft" | "scheduled" | "assigned" | "in_progress" | "submitted" | "in_review" | "approved" | "delivered" | "cancelled" | "completed";
  revision: number;
  assignedTo: string | null;
  teamId: string | null;
  scheduledFor: string | null;
  timezone: string | null;
  dueAt: string | null;
  template: InspectionTemplatePin | null;
  createdAt: string | null;
};
export const CreatedInspectionSchema = z.object({
  _id: z.string(),
  id: z.string(),
  organizationId: z.string(),
  subjectId: z.string().nullable(),
  propertyId: z.string().nullable(),
  inspectionTypeId: z.string().nullable(),
  status: z.enum(["draft", "scheduled", "assigned", "in_progress", "submitted", "in_review", "approved", "delivered", "cancelled", "completed"] as const),
  revision: z.number().int(),
  assignedTo: z.string().nullable(),
  teamId: z.string().nullable(),
  scheduledFor: z.string().nullable(),
  timezone: z.string().nullable(),
  dueAt: z.string().nullable(),
  template: InspectionTemplatePinSchema.nullable(),
  createdAt: z.string().nullable(),
}).strict();

export type AssignInspectionBody = {
  assignedTo: string;
  dueAt?: string | undefined;
  reason: string;
  expectedRevision: number;
};
export const AssignInspectionBodySchema = z.object({
  assignedTo: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
  dueAt: z.string().min(1).optional(),
  reason: z.string().min(1).max(4000),
  expectedRevision: z.number().int().min(0),
}).strict();

export type EligibleAssignee = {
  userId: string;
  name: string;
  email: string;
  roleNames: Array<string>;
  teamId: string | null;
  teamName: string | null;
  openAssignments: number;
  isCurrentAssignee: boolean;
};
export const EligibleAssigneeSchema = z.object({
  userId: z.string(),
  name: z.string(),
  email: z.string(),
  roleNames: z.array(z.string()),
  teamId: z.string().nullable(),
  teamName: z.string().nullable(),
  openAssignments: z.number().int(),
  isCurrentAssignee: z.boolean(),
}).strict();

export type ListEligibleAssigneesQuery = {
  cursor?: string | undefined;
  limit?: number | undefined;
  sort?: string | undefined;
  inspectionTypeId?: string | undefined;
  inspectionId?: string | undefined;
  q?: string | undefined;
};
export const ListEligibleAssigneesQuerySchema = z.object({
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  sort: z.string().min(1).optional(),
  inspectionTypeId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId").optional(),
  inspectionId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId").optional(),
  q: z.string().min(1).max(120).optional(),
});

export type MyDayWarning = {
  code: "photo_quality" | "analysis_pending" | "no_evidence_yet" | "assignee_unavailable" | "type_inactive";
  message: string;
};
export const MyDayWarningSchema = z.object({
  code: z.enum(["photo_quality", "analysis_pending", "no_evidence_yet", "assignee_unavailable", "type_inactive"] as const),
  message: z.string(),
}).strict();

export type MyDayNextAction = {
  action: "schedule" | "assign" | "start" | "submit" | "review" | "request_changes" | "approve" | "deliver" | "cancel";
  to: "draft" | "scheduled" | "assigned" | "in_progress" | "submitted" | "in_review" | "approved" | "delivered" | "cancelled" | "completed";
  label: string;
  allowed: boolean;
  reason: string | null;
  requirement: "scheduledFor" | "assignee" | "assigneeRole" | "note" | "reason" | null;
};
export const MyDayNextActionSchema = z.object({
  action: z.enum(["schedule", "assign", "start", "submit", "review", "request_changes", "approve", "deliver", "cancel"] as const),
  to: z.enum(["draft", "scheduled", "assigned", "in_progress", "submitted", "in_review", "approved", "delivered", "cancelled", "completed"] as const),
  label: z.string(),
  allowed: z.boolean(),
  reason: z.string().nullable(),
  requirement: z.enum(["scheduledFor", "assignee", "assigneeRole", "note", "reason"] as const).nullable(),
}).strict();

export type MyDayHandoff = {
  kind: "reassigned_away" | "cancelled";
  at: string | null;
  byName: string | null;
  toName: string | null;
  reason: string | null;
  message: string;
};
export const MyDayHandoffSchema = z.object({
  kind: z.enum(["reassigned_away", "cancelled"] as const),
  at: z.string().nullable(),
  byName: z.string().nullable(),
  toName: z.string().nullable(),
  reason: z.string().nullable(),
  message: z.string(),
}).strict();

export type MyDayCard = {
  id: string;
  status: "draft" | "scheduled" | "assigned" | "in_progress" | "submitted" | "in_review" | "approved" | "delivered" | "cancelled" | "completed";
  revision: number;
  group: "overdue" | "today" | "upcoming" | "in_progress";
  dueState: "overdue" | "due_today" | "scheduled" | "none";
  subjectId: string | null;
  siteName: string | null;
  typeName: string | null;
  scheduledFor: string | null;
  dueAt: string | null;
  timezone: string;
  localDay: string | null;
  localTime: string | null;
  assigneeUserId: string | null;
  assigneeName: string | null;
  teamName: string | null;
  warnings: Array<MyDayWarning>;
  nextAction: MyDayNextAction | null;
  handoff: MyDayHandoff | null;
};
export const MyDayCardSchema = z.object({
  id: z.string(),
  status: z.enum(["draft", "scheduled", "assigned", "in_progress", "submitted", "in_review", "approved", "delivered", "cancelled", "completed"] as const),
  revision: z.number().int(),
  group: z.enum(["overdue", "today", "upcoming", "in_progress"] as const),
  dueState: z.enum(["overdue", "due_today", "scheduled", "none"] as const),
  subjectId: z.string().nullable(),
  siteName: z.string().nullable(),
  typeName: z.string().nullable(),
  scheduledFor: z.string().nullable(),
  dueAt: z.string().nullable(),
  timezone: z.string(),
  localDay: z.string().nullable(),
  localTime: z.string().nullable(),
  assigneeUserId: z.string().nullable(),
  assigneeName: z.string().nullable(),
  teamName: z.string().nullable(),
  warnings: z.array(MyDayWarningSchema),
  nextAction: MyDayNextActionSchema.nullable(),
  handoff: MyDayHandoffSchema.nullable(),
}).strict();

export type MyDayView = {
  requestedMode: "own" | "team";
  mode: "own" | "team";
  scope: "own" | "team" | "org";
  label: string;
  canViewTeam: boolean;
  timezone: string;
  today: string;
  generatedAt: string;
  viewerUserId: string;
};
export const MyDayViewSchema = z.object({
  requestedMode: z.enum(["own", "team"] as const),
  mode: z.enum(["own", "team"] as const),
  scope: z.enum(["own", "team", "org"] as const),
  label: z.string(),
  canViewTeam: z.boolean(),
  timezone: z.string(),
  today: z.string(),
  generatedAt: z.string(),
  viewerUserId: z.string(),
}).strict();

export type ListMyDayQuery = {
  cursor?: string | undefined;
  limit?: number | undefined;
  sort?: string | undefined;
  mode?: "own" | "team" | undefined;
  group?: "overdue" | "today" | "upcoming" | "in_progress" | undefined;
};
export const ListMyDayQuerySchema = z.object({
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  sort: z.string().min(1).optional(),
  mode: z.enum(["own", "team"] as const).default("own"),
  group: z.enum(["overdue", "today", "upcoming", "in_progress"] as const).optional(),
});

export type AnswerFieldState = {
  fieldId: string;
  fieldKey: string;
  sectionId: string | null;
  sectionName: string;
  instanceId: string | null;
  label: string;
  type: string;
  visible: boolean;
  required: boolean;
  noteRequired: boolean;
  photoRequired: boolean;
  actionRequired: boolean;
  answered: boolean;
};
export const AnswerFieldStateSchema = z.object({
  fieldId: z.string(),
  fieldKey: z.string(),
  sectionId: z.string().nullable(),
  sectionName: z.string(),
  instanceId: z.string().nullable(),
  label: z.string(),
  type: z.string(),
  visible: z.boolean(),
  required: z.boolean(),
  noteRequired: z.boolean(),
  photoRequired: z.boolean(),
  actionRequired: z.boolean(),
  answered: z.boolean(),
}).strict();

export type AnswerRelationViolation = {
  type: string;
  message: string;
  fields: Array<string>;
};
export const AnswerRelationViolationSchema = z.object({
  type: z.string(),
  message: z.string(),
  fields: z.array(z.string()),
}).strict();

export type AnswerEvaluation = {
  fields: Array<AnswerFieldState>;
  violations: Array<AnswerRelationViolation>;
  completion: {
  requiredTotal: number;
  requiredAnswered: number;
  complete: boolean;
  missing: Array<{
  fieldId: string;
  fieldKey: string;
  sectionId: string | null;
  instanceId: string | null;
  label: string;
}>;
};
  hiddenAnswers: Array<{
  fieldId: string;
  instanceId: string | null;
}>;
};
export const AnswerEvaluationSchema = z.object({
  fields: z.array(AnswerFieldStateSchema),
  violations: z.array(AnswerRelationViolationSchema),
  completion: z.object({
    requiredTotal: z.number().int(),
    requiredAnswered: z.number().int(),
    complete: z.boolean(),
    missing: z.array(z.object({
      fieldId: z.string(),
      fieldKey: z.string(),
      sectionId: z.string().nullable(),
      instanceId: z.string().nullable(),
      label: z.string(),
    }).strict()),
  }).strict(),
  hiddenAnswers: z.array(z.object({
    fieldId: z.string(),
    instanceId: z.string().nullable(),
  }).strict()),
}).strict();

export type AnswerEntry = {
  fieldId: string;
  fieldKey: string;
  sectionId: string | null;
  instanceId: string | null;
  value: unknown;
  revision: number;
  updatedAt: string;
  updatedBy: string | null;
  hidden: boolean;
};
export const AnswerEntrySchema = z.object({
  fieldId: z.string(),
  fieldKey: z.string(),
  sectionId: z.string().nullable(),
  instanceId: z.string().nullable(),
  value: z.unknown(),
  revision: z.number().int(),
  updatedAt: z.string(),
  updatedBy: z.string().nullable(),
  hidden: z.boolean(),
}).strict();

export type AnswerRepeatSection = {
  sectionId: string;
  sectionName: string;
  instances: Array<string>;
  revision: number;
};
export const AnswerRepeatSectionSchema = z.object({
  sectionId: z.string(),
  sectionName: z.string(),
  instances: z.array(z.string()),
  revision: z.number().int(),
}).strict();

export type AnswerTemplatePin = {
  templateId: string | null;
  version: number | null;
  schemaHash: string | null;
  name: string | null;
  schema: unknown | null;
};
export const AnswerTemplatePinSchema = z.object({
  templateId: z.string().nullable(),
  version: z.number().int().nullable(),
  schemaHash: z.string().nullable(),
  name: z.string().nullable(),
  schema: z.unknown().nullable(),
}).strict();

export type AnswerNextActor = {
  label: string;
  name: string | null;
  message: string;
};
export const AnswerNextActorSchema = z.object({
  label: z.string(),
  name: z.string().nullable(),
  message: z.string(),
}).strict();

export type AnswerContext = {
  subjectId: string | null;
  siteName: string | null;
  typeName: string | null;
  dueAt: string | null;
  scheduledFor: string | null;
  dueState: "overdue" | "due_today" | "scheduled" | "none";
  timezone: string;
  nextActor: AnswerNextActor | null;
};
export const AnswerContextSchema = z.object({
  subjectId: z.string().nullable(),
  siteName: z.string().nullable(),
  typeName: z.string().nullable(),
  dueAt: z.string().nullable(),
  scheduledFor: z.string().nullable(),
  dueState: z.enum(["overdue", "due_today", "scheduled", "none"] as const),
  timezone: z.string(),
  nextActor: AnswerNextActorSchema.nullable(),
}).strict();

export type InspectionAnswerSet = {
  inspectionId: string;
  status: "draft" | "scheduled" | "assigned" | "in_progress" | "submitted" | "in_review" | "approved" | "delivered" | "cancelled" | "completed";
  readOnly: boolean;
  immutable: boolean;
  revision: number;
  template: AnswerTemplatePin | null;
  context: AnswerContext;
  answers: Array<AnswerEntry>;
  repeats: Array<AnswerRepeatSection>;
  evaluation: AnswerEvaluation;
  updatedAt: string | null;
  updatedBy: string | null;
};
export const InspectionAnswerSetSchema = z.object({
  inspectionId: z.string(),
  status: z.enum(["draft", "scheduled", "assigned", "in_progress", "submitted", "in_review", "approved", "delivered", "cancelled", "completed"] as const),
  readOnly: z.boolean(),
  immutable: z.boolean(),
  revision: z.number().int(),
  template: AnswerTemplatePinSchema.nullable(),
  context: AnswerContextSchema,
  answers: z.array(AnswerEntrySchema),
  repeats: z.array(AnswerRepeatSectionSchema),
  evaluation: AnswerEvaluationSchema,
  updatedAt: z.string().nullable(),
  updatedBy: z.string().nullable(),
}).strict();

export type AnswerChange = {
  op: "set" | "clear" | "add_row" | "remove_row" | "reorder_rows";
  fieldId?: string | undefined;
  instanceId?: string | undefined;
  sectionId?: string | undefined;
  instanceIds?: Array<string> | undefined;
  value?: unknown | undefined;
};
export const AnswerChangeSchema = z.object({
  op: z.enum(["set", "clear", "add_row", "remove_row", "reorder_rows"] as const),
  fieldId: z.string().min(1).optional(),
  instanceId: z.string().min(1).optional(),
  sectionId: z.string().min(1).optional(),
  instanceIds: z.array(z.string().min(1)).optional(),
  value: z.unknown().optional(),
}).strict().refine((c) =>
      (c.op === 'set' && Boolean(c.fieldId)) ||
      (c.op === 'clear' && Boolean(c.fieldId)) ||
      (c.op === 'add_row' && Boolean(c.sectionId)) ||
      (c.op === 'remove_row' && Boolean(c.sectionId) && Boolean(c.instanceId)) ||
      (c.op === 'reorder_rows' && Boolean(c.sectionId) && Array.isArray(c.instanceIds) && c.instanceIds.length > 0));

export type PatchAnswersBody = {
  baseRevision: number;
  changes: Array<AnswerChange>;
  idempotencyKey?: string | undefined;
};
export const PatchAnswersBodySchema = z.object({
  baseRevision: z.number().int().min(0),
  changes: z.array(AnswerChangeSchema),
  idempotencyKey: z.string().min(8).max(128).optional(),
}).strict();

export type AnswerConflict = {
  kind: "field" | "repeat_order";
  fieldId: string | null;
  fieldKey: string | null;
  sectionId: string | null;
  instanceId: string | null;
  changedAtRevision: number;
  mine: unknown;
  server: unknown;
};
export const AnswerConflictSchema = z.object({
  kind: z.enum(["field", "repeat_order"] as const),
  fieldId: z.string().nullable(),
  fieldKey: z.string().nullable(),
  sectionId: z.string().nullable(),
  instanceId: z.string().nullable(),
  changedAtRevision: z.number().int(),
  mine: z.unknown(),
  server: z.unknown(),
}).strict();

export type AnswerRevisionConflict = {
  reason: "answer_revision_conflict";
  baseRevision: number;
  currentRevision: number;
  conflicts: Array<AnswerConflict>;
  current: InspectionAnswerSet;
};
export const AnswerRevisionConflictSchema = z.object({
  reason: z.literal("answer_revision_conflict"),
  baseRevision: z.number().int(),
  currentRevision: z.number().int(),
  conflicts: z.array(AnswerConflictSchema),
  current: InspectionAnswerSetSchema,
}).strict();

export type CaptureLockedRefusal = {
  reason: "capture_locked";
  status: "draft" | "scheduled" | "assigned" | "in_progress" | "submitted" | "in_review" | "approved" | "delivered" | "cancelled" | "completed";
  immutable: boolean;
};
export const CaptureLockedRefusalSchema = z.object({
  reason: z.literal("capture_locked"),
  status: z.enum(["draft", "scheduled", "assigned", "in_progress", "submitted", "in_review", "approved", "delivered", "cancelled", "completed"] as const),
  immutable: z.boolean(),
}).strict();

export type AnswerEvidenceAlternative = {
  type: string;
  legacyType: string;
  label: string;
  description: string;
};
export const AnswerEvidenceAlternativeSchema = z.object({
  type: z.string(),
  legacyType: z.string(),
  label: z.string(),
  description: z.string(),
}).strict();

export type AnswerIssue = {
  code: "no_pinned_template" | "template_pin_mismatch" | "unknown_field" | "unknown_section" | "not_repeatable" | "repeat_instance_required" | "unexpected_repeat_instance" | "unknown_row" | "duplicate_row" | "row_order_mismatch" | "row_limit" | "not_answerable" | "invalid_type" | "invalid_option" | "out_of_range" | "unsupported_evidence" | "evidence_not_found";
  message: string;
  changeIndex: number | null;
  fieldId: string | null;
  fieldKey: string | null;
  fieldLabel: string | null;
  sectionId: string | null;
  instanceId: string | null;
  allowed: Array<string> | null;
  evidence: {
  kind: string;
  label: string;
  detail: string;
  alternatives: Array<AnswerEvidenceAlternative>;
  unblockedByStory: string;
} | null;
};
export const AnswerIssueSchema = z.object({
  code: z.enum(["no_pinned_template", "template_pin_mismatch", "unknown_field", "unknown_section", "not_repeatable", "repeat_instance_required", "unexpected_repeat_instance", "unknown_row", "duplicate_row", "row_order_mismatch", "row_limit", "not_answerable", "invalid_type", "invalid_option", "out_of_range", "unsupported_evidence", "evidence_not_found"] as const),
  message: z.string(),
  changeIndex: z.number().int().nullable(),
  fieldId: z.string().nullable(),
  fieldKey: z.string().nullable(),
  fieldLabel: z.string().nullable(),
  sectionId: z.string().nullable(),
  instanceId: z.string().nullable(),
  allowed: z.array(z.string()).nullable(),
  evidence: z.object({
    kind: z.string(),
    label: z.string(),
    detail: z.string(),
    alternatives: z.array(AnswerEvidenceAlternativeSchema),
    unblockedByStory: z.string(),
  }).strict().nullable(),
}).strict();

export type AnswerPatchRefusal = {
  reason: "invalid_answers";
  issues: Array<AnswerIssue>;
};
export const AnswerPatchRefusalSchema = z.object({
  reason: z.literal("invalid_answers"),
  issues: z.array(AnswerIssueSchema),
}).strict();

export type InspectionEvidenceMetadata = {
  filename: string | null;
  mimeType: string;
  size: number;
  originalSize: number | null;
  width: number | null;
  height: number | null;
  caption: string | null;
  capturedAt: string | null;
};
export const InspectionEvidenceMetadataSchema = z.object({
  filename: z.string().nullable(),
  mimeType: z.string(),
  size: z.number().int(),
  originalSize: z.number().int().nullable(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  caption: z.string().nullable(),
  capturedAt: z.string().nullable(),
}).strict();

export type InspectionEvidence = {
  id: string;
  inspectionId: string;
  fieldId: string;
  instanceId: string | null;
  kind: "photo";
  clientId: string;
  metadata: InspectionEvidenceMetadata;
  thumbnail: string | null;
  status: "stored";
  createdAt: string;
  createdBy: string | null;
};
export const InspectionEvidenceSchema = z.object({
  id: z.string(),
  inspectionId: z.string(),
  fieldId: z.string(),
  instanceId: z.string().nullable(),
  kind: z.enum(["photo"] as const),
  clientId: z.string(),
  metadata: InspectionEvidenceMetadataSchema,
  thumbnail: z.string().nullable(),
  status: z.literal("stored"),
  createdAt: z.string(),
  createdBy: z.string().nullable(),
}).strict();

export type EvidenceLimits = {
  maxPerField: number;
  maxBytes: number;
  mimeTypes: Array<string>;
  maxCaptionLength: number;
};
export const EvidenceLimitsSchema = z.object({
  maxPerField: z.number().int(),
  maxBytes: z.number().int(),
  mimeTypes: z.array(z.string()),
  maxCaptionLength: z.number().int(),
}).strict();

export type UploadEvidenceBody = {
  fieldId: string;
  instanceId?: string | undefined;
  clientId: string;
  caption?: string | undefined;
  capturedAt?: string | undefined;
  originalSize?: number | undefined;
  width?: number | undefined;
  height?: number | undefined;
  thumbnail?: string | undefined;
};
export const UploadEvidenceBodySchema = z.object({
  fieldId: z.string().min(1),
  instanceId: z.string().min(1).optional(),
  clientId: z.string().min(8).max(128),
  caption: z.string().max(500).optional(),
  capturedAt: z.string().optional(),
  originalSize: z.coerce.number().int().min(0).optional(),
  width: z.coerce.number().int().min(0).optional(),
  height: z.coerce.number().int().min(0).optional(),
  thumbnail: z.string().max(200000).optional(),
}).strict();

export type UpdateEvidenceBody = {
  caption: string | null;
};
export const UpdateEvidenceBodySchema = z.object({
  caption: z.string().max(500).nullable(),
}).strict();

export type EvidenceReferencedRefusal = {
  reason: "evidence_referenced";
  message: string;
  findings: Array<{
  id: string;
  observationText: string;
  status: string;
}>;
};
export const EvidenceReferencedRefusalSchema = z.object({
  reason: z.literal("evidence_referenced"),
  message: z.string(),
  findings: z.array(z.object({
    id: z.string(),
    observationText: z.string(),
    status: z.string(),
  }).strict()),
}).strict();

export type ReviewQueueCard = {
  id: string;
  status: "draft" | "scheduled" | "assigned" | "in_progress" | "submitted" | "in_review" | "approved" | "delivered" | "cancelled" | "completed";
  revision: number;
  subjectId: string | null;
  subjectName: string | null;
  typeName: string | null;
  submittedAt: string | null;
  reviewStartedAt: string | null;
  attempt: number;
  assigneeUserId: string | null;
  assigneeName: string | null;
  teamName: string | null;
  assembly: "none" | "queued" | "running" | "failed" | "ready";
  assemblyError: string | null;
  unresolvedDecisions: number;
  reviewerUserId: string | null;
  reviewerName: string | null;
};
export const ReviewQueueCardSchema = z.object({
  id: z.string(),
  status: z.enum(["draft", "scheduled", "assigned", "in_progress", "submitted", "in_review", "approved", "delivered", "cancelled", "completed"] as const),
  revision: z.number().int(),
  subjectId: z.string().nullable(),
  subjectName: z.string().nullable(),
  typeName: z.string().nullable(),
  submittedAt: z.string().nullable(),
  reviewStartedAt: z.string().nullable(),
  attempt: z.number().int(),
  assigneeUserId: z.string().nullable(),
  assigneeName: z.string().nullable(),
  teamName: z.string().nullable(),
  assembly: z.enum(["none", "queued", "running", "failed", "ready"] as const),
  assemblyError: z.string().nullable(),
  unresolvedDecisions: z.number().int(),
  reviewerUserId: z.string().nullable(),
  reviewerName: z.string().nullable(),
}).strict();

export type ReviewQueueView = {
  scope: "own" | "team" | "org";
  label: string;
  generatedAt: string;
  viewerUserId: string;
};
export const ReviewQueueViewSchema = z.object({
  scope: z.enum(["own", "team", "org"] as const),
  label: z.string(),
  generatedAt: z.string(),
  viewerUserId: z.string(),
}).strict();

export type ListReviewQueueQuery = {
  cursor?: string | undefined;
  limit?: number | undefined;
  sort?: string | undefined;
  status?: "submitted" | "in_review" | "all" | undefined;
  q?: string | undefined;
};
export const ListReviewQueueQuerySchema = z.object({
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  sort: z.string().min(1).optional(),
  status: z.enum(["submitted", "in_review", "all"] as const).default("all"),
  q: z.string().min(1).optional(),
});

export type FindingEdits = {
  observationText?: string | undefined;
  recommendationText?: string | null | undefined;
  severityTag?: string | null | undefined;
  area?: string | null | undefined;
  buildingElement?: string | null | undefined;
};
export const FindingEditsSchema = z.object({
  observationText: z.string().min(1).max(4000).optional(),
  recommendationText: z.string().max(4000).nullable().optional(),
  severityTag: z.string().max(120).nullable().optional(),
  area: z.string().max(200).nullable().optional(),
  buildingElement: z.string().max(200).nullable().optional(),
}).strict();

export type FindingDecisionBody = {
  decision: "accept" | "edit" | "discard";
  edits?: FindingEdits | undefined;
  note?: string | undefined;
};
export const FindingDecisionBodySchema = z.object({
  decision: z.enum(["accept", "edit", "discard"] as const),
  edits: FindingEditsSchema.optional(),
  note: z.string().min(1).max(2000).optional(),
}).strict();

export type InspectionFinding = {
  id: string;
  observationText: string;
  recommendationText: string | null;
  status: "draft" | "approved" | "discarded";
  severityTag: string | null;
  area: string | null;
  buildingElement: string | null;
  linkedEvidenceIds: Array<string>;
  draftFromAi: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
};
export const InspectionFindingSchema = z.object({
  id: z.string(),
  observationText: z.string(),
  recommendationText: z.string().nullable(),
  status: z.enum(["draft", "approved", "discarded"] as const),
  severityTag: z.string().nullable(),
  area: z.string().nullable(),
  buildingElement: z.string().nullable(),
  linkedEvidenceIds: z.array(z.string()),
  draftFromAi: z.string().nullable(),
  decidedBy: z.string().nullable(),
  decidedAt: z.string().nullable(),
  decisionNote: z.string().nullable(),
}).strict();

export type ApprovalBlocker = {
  code: "assembly_not_ready" | "unresolved_decisions" | "content_invalid";
  message: string;
  findingIds: Array<string>;
};
export const ApprovalBlockerSchema = z.object({
  code: z.enum(["assembly_not_ready", "unresolved_decisions", "content_invalid"] as const),
  message: z.string(),
  findingIds: z.array(z.string()),
}).strict();

export type ApprovalBlockedRefusal = {
  reason: "approval_blocked";
  blockers: Array<ApprovalBlocker>;
};
export const ApprovalBlockedRefusalSchema = z.object({
  reason: z.literal("approval_blocked"),
  blockers: z.array(ApprovalBlockerSchema),
}).strict();

export type ListInspectionsResponse = {
  items: Array<InspectionListItem>;
  nextCursor: string | null;
  total?: number | undefined;
};

export async function listInspections(query?: ListInspectionsQuery): Promise<ListInspectionsResponse> {
  const response = await api.get<ListInspectionsResponse>(`/inspections`, { params: query });
  return response.data;
}

export type GetInspectionLifecycleResponse = {
  data: InspectionLifecycle;
};

export const GetInspectionLifecycleParamsSchema = z.object({
  id: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function getInspectionLifecycle(id: string): Promise<GetInspectionLifecycleResponse> {
  const response = await api.get<GetInspectionLifecycleResponse>(`/inspections/${encodeURIComponent(id)}/lifecycle`);
  return response.data;
}

export type TransitionInspectionResponse = {
  data: InspectionLifecycle;
  replayed: boolean;
};

export const TransitionInspectionParamsSchema = z.object({
  id: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function transitionInspection(id: string, body: TransitionInspectionBody): Promise<TransitionInspectionResponse> {
  const response = await api.post<TransitionInspectionResponse>(`/inspections/${encodeURIComponent(id)}/transitions`, body);
  return response.data;
}

export type ListMyDayResponse = {
  items: Array<MyDayCard>;
  nextCursor: string | null;
  total?: number | undefined;
  view: MyDayView;
};

export async function listMyDay(query?: ListMyDayQuery): Promise<ListMyDayResponse> {
  const response = await api.get<ListMyDayResponse>(`/inspections/my-day`, { params: query });
  return response.data;
}

export type ListEligibleAssigneesResponse = {
  items: Array<EligibleAssignee>;
  nextCursor: string | null;
  total?: number | undefined;
};

export async function listEligibleAssignees(query?: ListEligibleAssigneesQuery): Promise<ListEligibleAssigneesResponse> {
  const response = await api.get<ListEligibleAssigneesResponse>(`/inspections/eligible-assignees`, { params: query });
  return response.data;
}

export type CreateInspectionResponse = {
  data: CreatedInspection;
  replayed: boolean;
};

export async function createInspection(body: CreateInspectionBody): Promise<CreateInspectionResponse> {
  const response = await api.post<CreateInspectionResponse>(`/inspections`, body);
  return response.data;
}

export type AssignInspectionResponse = {
  data: InspectionLifecycle;
};

export const AssignInspectionParamsSchema = z.object({
  id: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function assignInspection(id: string, body: AssignInspectionBody): Promise<AssignInspectionResponse> {
  const response = await api.post<AssignInspectionResponse>(`/inspections/${encodeURIComponent(id)}/assign`, body);
  return response.data;
}

export type GetInspectionAnswersResponse = {
  data: InspectionAnswerSet;
};

export const GetInspectionAnswersParamsSchema = z.object({
  id: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function getInspectionAnswers(id: string): Promise<GetInspectionAnswersResponse> {
  const response = await api.get<GetInspectionAnswersResponse>(`/inspections/${encodeURIComponent(id)}/answers`);
  return response.data;
}

export type PatchInspectionAnswersResponse = {
  data: InspectionAnswerSet;
  replayed: boolean;
};

export const PatchInspectionAnswersParamsSchema = z.object({
  id: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function patchInspectionAnswers(id: string, body: PatchAnswersBody): Promise<PatchInspectionAnswersResponse> {
  const response = await api.patch<PatchInspectionAnswersResponse>(`/inspections/${encodeURIComponent(id)}/answers`, body);
  return response.data;
}

export type ListInspectionEvidenceResponse = {
  items: Array<InspectionEvidence>;
  limits: EvidenceLimits;
};

export const ListInspectionEvidenceParamsSchema = z.object({
  id: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function listInspectionEvidence(id: string): Promise<ListInspectionEvidenceResponse> {
  const response = await api.get<ListInspectionEvidenceResponse>(`/inspections/${encodeURIComponent(id)}/evidence`);
  return response.data;
}

export type UploadInspectionEvidenceResponse = {
  data: InspectionEvidence;
  answers: InspectionAnswerSet;
  replayed: boolean;
};

export const UploadInspectionEvidenceParamsSchema = z.object({
  id: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function uploadInspectionEvidence(id: string, body: UploadEvidenceBody, file: Blob, options?: { signal?: AbortSignal; onUploadProgress?: (progress: { loaded: number; total?: number }) => void }): Promise<UploadInspectionEvidenceResponse> {
  const form = new FormData();
  form.append("file", file);
  Object.entries(body as Record<string, unknown>).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    form.append(key, String(value));
  });
  const config: AxiosRequestConfig = { headers: { 'Content-Type': 'multipart/form-data' } };
  if (options?.signal) config.signal = options.signal;
  const onProgress = options?.onUploadProgress;
  if (onProgress) {
    config.onUploadProgress = (event) =>
      onProgress(event.total === undefined ? { loaded: event.loaded } : { loaded: event.loaded, total: event.total });
  }
  const response = await api.post<UploadInspectionEvidenceResponse>(`/inspections/${encodeURIComponent(id)}/evidence`, form, config);
  return response.data;
}

export type UpdateInspectionEvidenceResponse = {
  data: InspectionEvidence;
};

export const UpdateInspectionEvidenceParamsSchema = z.object({
  id: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
  evidenceId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function updateInspectionEvidence(id: string, evidenceId: string, body: UpdateEvidenceBody): Promise<UpdateInspectionEvidenceResponse> {
  const response = await api.patch<UpdateInspectionEvidenceResponse>(`/inspections/${encodeURIComponent(id)}/evidence/${encodeURIComponent(evidenceId)}`, body);
  return response.data;
}

export type DeleteInspectionEvidenceResponse = {
  data: InspectionEvidence;
  answers: InspectionAnswerSet;
};

export const DeleteInspectionEvidenceParamsSchema = z.object({
  id: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
  evidenceId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function deleteInspectionEvidence(id: string, evidenceId: string): Promise<DeleteInspectionEvidenceResponse> {
  const response = await api.delete<DeleteInspectionEvidenceResponse>(`/inspections/${encodeURIComponent(id)}/evidence/${encodeURIComponent(evidenceId)}`);
  return response.data;
}

export type ListReviewQueueResponse = {
  items: Array<ReviewQueueCard>;
  nextCursor: string | null;
  total?: number | undefined;
  view: ReviewQueueView;
};

export async function listReviewQueue(query?: ListReviewQueueQuery): Promise<ListReviewQueueResponse> {
  const response = await api.get<ListReviewQueueResponse>(`/inspections/review-queue`, { params: query });
  return response.data;
}

export type ListInspectionFindingsResponse = {
  items: Array<InspectionFinding>;
};

export const ListInspectionFindingsParamsSchema = z.object({
  id: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function listInspectionFindings(id: string): Promise<ListInspectionFindingsResponse> {
  const response = await api.get<ListInspectionFindingsResponse>(`/inspections/${encodeURIComponent(id)}/review-findings`);
  return response.data;
}

export type DecideFindingResponse = {
  data: InspectionFinding;
};

export const DecideFindingParamsSchema = z.object({
  id: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
  findingId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function decideFinding(id: string, findingId: string, body: FindingDecisionBody): Promise<DecideFindingResponse> {
  const response = await api.post<DecideFindingResponse>(`/inspections/${encodeURIComponent(id)}/findings/${encodeURIComponent(findingId)}/decision`, body);
  return response.data;
}

export const inspectionsKeys = {
  all: ["inspections"] as const,
  lists: () => [...inspectionsKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) =>
    [...inspectionsKeys.lists(), ...(filters ? [filters] : [])] as const,
  details: () => [...inspectionsKeys.all, 'detail'] as const,
  detail: (id: string) => [...inspectionsKeys.details(), id] as const,
};
